import useAuth from '../../../hooks/utils/useAuth';
import { ProviderName, useProviderPlan, useSsoRedirect } from '../../../hooks/api/useExternalProviderPlans';
import { Features, useFeatureFlag } from '../../../hooks/api/useFeatureFlag';
import getPartnerResourceConfig from './partner-resource-config';

export const useProviderDetails = (providerType?: string) => {
  const { user } = useAuth();
  const { providerPlan, providerName, providerTypes, isLoading: providerPlanLoading } = useProviderPlan();

  const isLoading = user?.isLoading || providerPlanLoading;
  const clientLogoUrl = user?.caller_role.active_subscription?.package?.client?.logo_file_url;
  const partnerResourceConfig = getPartnerResourceConfig(user, providerPlan);

  const recuroSso = useFeatureFlag(Features.RECURO_SSO);

  // For Recuro: prefer the DB-configured online_access_url for the current
  // provider type (seeded with correct per-type URLs including the crisis override).
  // Fall back to member.recurohealth.com if not yet configured.
  // When the RECURO_SSO feature flag is on, the Keycloak SAML URL takes priority
  // for non-crisis types (env-based, cannot be stored in DB).
  const typeConfig = providerPlan?.type_configs?.find((c) => c.provider_type === providerType);
  const configuredRecuroUrl = typeConfig?.online_access_url ?? 'https://member.recurohealth.com';
  let recuroLink: string;
  if (recuroSso.enabled && providerType !== 'crisis') {
    recuroLink = `${process.env.REACT_APP_KEYCLOAK_IDP_URL}/realms/${process.env.REACT_APP_KEYCLOAK_IDP_REALM}/protocol/saml/clients/idp-init`;
  } else {
    recuroLink = configuredRecuroUrl;
  }

  // Revive SSO is Keycloak IdP-initiated; the per-environment SAML URL lives in
  // the type config's online_access_url (backend /sso_redirect rejects revive).
  const reviveLink = typeConfig?.online_access_url ?? undefined;

  const isSsoEnabled =
    providerName !== 'recuro' &&
    providerName !== 'revive' &&
    (providerType === 'primary' || providerType === 'counseling' || providerType === 'urgent');
  const {
    data: ssoLink,
    isLoading: ssoLinkLoading,
    error: ssoError,
  } = useSsoRedirect(user?.id || 0, isSsoEnabled, providerType!);

  const url = providerName === 'recuro' ? recuroLink : providerName === 'revive' ? reviveLink : ssoLink?.redirect_url;
  const urlDescription = `Visit ${providerName} online`;

  return {
    clientLogoUrl,
    isLoading,
    partnerResourceConfig,
    providerName,
    providerTypes,
    ssoLinkLoading: isSsoEnabled ? ssoLinkLoading : undefined,
    ssoError,
    url,
    urlDescription,
  };
};
